/**
 * Enhanced Monitoring System for Wixell Platform
 * Extends existing monitoring with comprehensive error tracking, performance monitoring, and alerting
 */

import * as Sentry from '@sentry/react';
import { browserTracingIntegration } from '@sentry/react';
import { replayIntegration } from '@sentry/react';
import { reportError, captureEvent } from './monitoring.js';

class EnhancedMonitoring {
  constructor() {
    this.isInitialized = false;
    this.performanceMetrics = new Map();
    this.errorCounts = new Map();
    this.alertThresholds = {
      errorRate: 0.05, // 5%
      responseTime: 5000, // 5 seconds
      memoryUsage: 0.8, // 80%
      cpuUsage: 0.8, // 80%
    };
    this.criticalErrors = [];
    this.performanceSpans = new Map();
    this.userFlows = new Map();
    this.alerts = [];
    
    this.init();
  }

  async init() {
    try {
      // Initialize Sentry with enhanced configuration
      this.initEnhancedSentry();
      
      // Set up performance monitoring
      this.initPerformanceMonitoring();
      
      // Set up error tracking
      this.initErrorTracking();
      
      // Set up user flow monitoring
      this.initUserFlowMonitoring();
      
      // Set up alerting system
      this.initAlertingSystem();
      
      // Set up retry mechanism for failed requests
      this.initRetryMechanism();
      
      this.isInitialized = true;
      console.log('[EnhancedMonitoring] Initialized successfully');
    } catch (error) {
      console.error('[EnhancedMonitoring] Initialization failed:', error);
    }
  }

  initEnhancedSentry() {
    try {
      const SENTRY_DSN = typeof import.meta !== 'undefined' && import.meta.env ? import.meta.env.VITE_SENTRY_DSN : process.env.VITE_SENTRY_DSN;
      const ENVIRONMENT = (typeof import.meta !== 'undefined' && import.meta.env) ? (import.meta.env.MODE || 'development') : (process.env.NODE_ENV || 'development');
      
      console.log('[EnhancedMonitoring] Initializing Sentry...');
      console.log('[EnhancedMonitoring] Sentry DSN configured:', !!SENTRY_DSN);
      console.log('[EnhancedMonitoring] Sentry available:', typeof Sentry !== 'undefined');
      console.log('[EnhancedMonitoring] BrowserTracing available:', typeof BrowserTracing !== 'undefined');
      console.log('[EnhancedMonitoring] Replay available:', typeof Replay !== 'undefined');
      
      if (!SENTRY_DSN) {
        console.warn('[EnhancedMonitoring] Sentry DSN not configured');
        return;
      }

      if (typeof Sentry === 'undefined') {
        console.error('[EnhancedMonitoring] Sentry is not available');
        return;
      }

      const integrations = [];
      
      // Add BrowserTracing if available
      if (typeof browserTracingIntegration === 'function') {
        try {
          integrations.push(browserTracingIntegration({
            tracingOrigins: ['localhost', /^\//, 'https://wixell.com', 'https://staging.wixell.com'],
          }));
          console.log('[EnhancedMonitoring] BrowserTracing integration added');
        } catch (tracingError) {
          console.warn('[EnhancedMonitoring] BrowserTracing initialization failed:', tracingError);
        }
      } else {
        console.warn('[EnhancedMonitoring] BrowserTracing is not available');
      }

      // Add Replay integration if available
      if (typeof replayIntegration === 'function') {
        try {
          integrations.push(replayIntegration({
            sessionSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,
            errorSampleRate: 1.0,
          }));
          console.log('[EnhancedMonitoring] Replay integration added');
        } catch (replayError) {
          console.warn('[EnhancedMonitoring] Replay initialization failed:', replayError);
        }
      } else {
        console.warn('[EnhancedMonitoring] Replay is not available');
      }

      // Initialize Sentry with enhanced configuration
      Sentry.init({
        dsn: SENTRY_DSN,
        environment: ENVIRONMENT,
        integrations,
        tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,
        release: this.getReleaseVersion(),
        dist: this.getDistribution(),
        enabled: ENVIRONMENT === 'production' || ((typeof import.meta !== 'undefined' && import.meta.env) ? import.meta.env.VITE_DEBUG_MODE === 'true' : process.env.VITE_DEBUG_MODE === 'true'),
        
        // Enhanced error handling
        beforeSend: this.enrichErrorBeforeSend.bind(this),
        beforeBreadcrumb: this.enrichBreadcrumb.bind(this),
        
        // Performance monitoring
        maxBreadcrumbs: 100,
        debug: (typeof import.meta !== 'undefined' && import.meta.env) ? import.meta.env.VITE_DEBUG_MODE === 'true' : process.env.VITE_DEBUG_MODE === 'true',
        
        // Transport configuration
        transport: Sentry.makeFetchTransport,
        stackParser: Sentry.defaultStackParser,
      });
      
      console.log('[EnhancedMonitoring] Sentry initialized successfully');
    } catch (error) {
      console.error('[EnhancedMonitoring] Sentry initialization failed:', error);
      // Don't re-throw the error to prevent app crashes
    }
  }

  initPerformanceMonitoring() {
    // Monitor Core Web Vitals
    this.monitorCoreWebVitals();
    
    // Monitor API performance
    this.monitorAPIPerformance();
    
    // Monitor render performance
    this.monitorRenderPerformance();
    
    // Monitor memory usage
    this.monitorMemoryUsage();
    
    // Monitor network performance
    this.monitorNetworkPerformance();
  }

  initErrorTracking() {
    // Enhanced global error handler
    window.addEventListener('error', this.handleGlobalError.bind(this));
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection.bind(this));
    
    // Monitor authentication errors
    this.monitorAuthErrors();
    
    // Monitor API errors
    this.monitorAPIErrors();
    
    // Monitor resource loading errors
    this.monitorResourceErrors();
  }

  initUserFlowMonitoring() {
    // Track critical user flows
    this.trackUserFlow('authentication', [
      'login_attempt',
      'login_success',
      'login_failure',
      'logout',
      'registration',
      'password_reset'
    ]);
    
    this.trackUserFlow('invoice_management', [
      'invoice_create',
      'invoice_edit',
      'invoice_send',
      'invoice_payment',
      'invoice_export'
    ]);
    
    this.trackUserFlow('customer_management', [
      'customer_create',
      'customer_edit',
      'customer_delete',
      'customer_search'
    ]);
  }

  initAlertingSystem() {
    // Set up periodic alert checking
    setInterval(() => {
      this.checkAlerts();
    }, 60000); // Check every minute
    
    // Set up error rate monitoring
    setInterval(() => {
      this.checkErrorRate();
    }, 300000); // Check every 5 minutes
  }

  initRetryMechanism() {
    // Retry failed requests every 30 seconds
    setInterval(() => {
      this.retryFailedRequests();
    }, 30000);
    
    // Also retry when page becomes visible again
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.retryFailedRequests();
      }
    });
    
    // Retry when network connection is restored
    if ('connection' in navigator) {
      navigator.connection.addEventListener('change', () => {
        if (navigator.connection.effectiveType !== 'slow-2g') {
          this.retryFailedRequests();
        }
      });
    }
  }

  // Performance Monitoring Methods
  monitorCoreWebVitals() {
    // Monitor Largest Contentful Paint (LCP)
    this.observePerformanceEntry('largest-contentful-paint', (entry) => {
      const lcp = entry.startTime;
      this.recordMetric('lcp', lcp);
      
      if (lcp > 2500) {
        this.addAlert('PERFORMANCE', 'LCP too slow', {
          value: lcp,
          threshold: 2500,
          url: window.location.href
        });
      }
    });

    // Monitor First Input Delay (FID)
    this.observePerformanceEntry('first-input', (entry) => {
      const fid = entry.processingStart - entry.startTime;
      this.recordMetric('fid', fid);
      
      if (fid > 100) {
        this.addAlert('PERFORMANCE', 'FID too high', {
          value: fid,
          threshold: 100,
          url: window.location.href
        });
      }
    });

    // Monitor Cumulative Layout Shift (CLS)
    let clsValue = 0;
    this.observePerformanceEntry('layout-shift', (entry) => {
      if (!entry.hadRecentInput) {
        clsValue += entry.value;
        this.recordMetric('cls', clsValue);
        
        if (clsValue > 0.1) {
          this.addAlert('PERFORMANCE', 'CLS too high', {
            value: clsValue,
            threshold: 0.1,
            url: window.location.href
          });
        }
      }
    });
  }

  observePerformanceEntry(type, callback) {
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          callback(entry);
        }
      });
      observer.observe({ type, buffered: true });
    } catch (error) {
      console.warn(`[EnhancedMonitoring] Cannot observe ${type}:`, error);
    }
  }

  monitorAPIPerformance() {
    // Intercept fetch calls to monitor API performance
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const startTime = performance.now();
      const url = args[0];
      
      try {
        const response = await originalFetch(...args);
        const duration = performance.now() - startTime;
        
        this.recordAPIMetric(url, duration, response.status);
        
        if (duration > this.alertThresholds.responseTime) {
          this.addAlert('PERFORMANCE', 'Slow API response', {
            url,
            duration,
            threshold: this.alertThresholds.responseTime,
            status: response.status
          });
        }
        
        return response;
      } catch (error) {
        const duration = performance.now() - startTime;
        this.recordAPIMetric(url, duration, null, error);
        
        this.addAlert('API_ERROR', 'API request failed', {
          url,
          duration,
          error: error.message
        });
        
        throw error;
      }
    };
  }

  monitorRenderPerformance() {
    // Monitor React component render times
    if (window.performance && window.performance.mark) {
      // Track initial page load
      window.addEventListener('load', () => {
        setTimeout(() => {
          const loadTime = performance.timing.loadEventEnd - performance.timing.navigationStart;
          this.recordMetric('page_load_time', loadTime);
          
          if (loadTime > 3000) {
            this.addAlert('PERFORMANCE', 'Page load too slow', {
              value: loadTime,
              threshold: 3000,
              url: window.location.href
            });
          }
        }, 0);
      });
    }
  }

  monitorMemoryUsage() {
    if (performance.memory) {
      setInterval(() => {
        const memoryUsage = performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit;
        this.recordMetric('memory_usage', memoryUsage);
        
        if (memoryUsage > this.alertThresholds.memoryUsage) {
          this.addAlert('PERFORMANCE', 'High memory usage', {
            value: memoryUsage,
            threshold: this.alertThresholds.memoryUsage,
            used: performance.memory.usedJSHeapSize,
            limit: performance.memory.jsHeapSizeLimit
          });
        }
      }, 30000); // Check every 30 seconds
    }
  }

  monitorNetworkPerformance() {
    if ('connection' in navigator) {
      const connection = navigator.connection;
      
      // Monitor effective connection type changes
      connection.addEventListener('change', () => {
        this.recordMetric('connection_type', connection.effectiveType);
        this.recordMetric('downlink', connection.downlink);
      });
      
      // Initial network metrics
      this.recordMetric('connection_type', connection.effectiveType);
      this.recordMetric('downlink', connection.downlink);
    }
  }

  // Error Tracking Methods
  handleGlobalError(event) {
    const error = {
      type: 'javascript',
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getCurrentUserId(),
      tenantId: this.getCurrentTenantId()
    };

    this.trackError(error);
  }

  handleUnhandledRejection(event) {
    const error = {
      type: 'unhandled_promise',
      message: event.reason?.message || 'Unhandled Promise Rejection',
      stack: event.reason?.stack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getCurrentUserId(),
      tenantId: this.getCurrentTenantId()
    };

    this.trackError(error);
  }

  monitorAuthErrors() {
    // Listen for authentication errors
    window.addEventListener('auth_error', (event) => {
      this.trackError({
        type: 'authentication',
        message: event.detail.message,
        context: event.detail.context,
        timestamp: new Date().toISOString(),
        userId: this.getCurrentUserId(),
        tenantId: this.getCurrentTenantId()
      });
    });
  }

  monitorAPIErrors() {
    // Listen for API errors from existing error tracking
    window.addEventListener('api_error', (event) => {
      this.trackError({
        type: 'api',
        message: event.detail.message,
        context: event.detail.context,
        timestamp: new Date().toISOString(),
        userId: this.getCurrentUserId(),
        tenantId: this.getCurrentTenantId()
      });
    });
  }

  monitorResourceErrors() {
    window.addEventListener('error', (event) => {
      if (event.target !== window) {
        // Resource loading error
        this.trackError({
          type: 'resource',
          message: `Failed to load resource: ${event.target.src || event.target.href}`,
          element: event.target.tagName,
          source: event.target.src || event.target.href,
          timestamp: new Date().toISOString(),
          userId: this.getCurrentUserId(),
          tenantId: this.getCurrentTenantId()
        });
      }
    }, true);
  }

  // User Flow Tracking
  trackUserFlow(flowName, steps) {
    this.userFlows.set(flowName, {
      steps,
      currentStep: null,
      startTime: null,
      stepTimes: new Map()
    });
  }

  startUserFlow(flowName, step) {
    const flow = this.userFlows.get(flowName);
    if (!flow) return;

    flow.currentStep = step;
    flow.startTime = performance.now();
    
    this.recordEvent('user_flow_start', {
      flow: flowName,
      step,
      timestamp: new Date().toISOString()
    });
  }

  completeUserFlowStep(flowName, step) {
    const flow = this.userFlows.get(flowName);
    if (!flow || flow.currentStep !== step) return;

    const stepTime = performance.now() - flow.startTime;
    flow.stepTimes.set(step, stepTime);
    
    this.recordEvent('user_flow_step_complete', {
      flow: flowName,
      step,
      duration: stepTime,
      timestamp: new Date().toISOString()
    });
    
    // Check if step took too long
    const stepThresholds = {
      login_attempt: 5000,
      invoice_create: 3000,
      customer_create: 2000
    };
    
    if (stepThresholds[step] && stepTime > stepThresholds[step]) {
      this.addAlert('UX', 'User flow step too slow', {
        flow: flowName,
        step,
        duration: stepTime,
        threshold: stepThresholds[step]
      });
    }
  }

  // Alerting System
  addAlert(type, message, data = {}) {
    const alert = {
      id: `${type}_${Date.now()}`,
      type,
      message,
      data,
      timestamp: new Date().toISOString(),
      severity: this.getAlertSeverity(type),
      userId: this.getCurrentUserId(),
      tenantId: this.getCurrentTenantId()
    };

    this.alerts.push(alert);
    
    // Keep only last 100 alerts
    if (this.alerts.length > 100) {
      this.alerts = this.alerts.slice(-100);
    }

    // Send to Sentry for critical alerts
    if (alert.severity === 'CRITICAL') {
      Sentry.captureMessage(`[ALERT] ${type}: ${message}`, 'error');
    }

    // Send to monitoring service
    this.sendAlertToService(alert);
  }

  getAlertSeverity(type) {
    const severityMap = {
      'PERFORMANCE': 'MEDIUM',
      'API_ERROR': 'HIGH',
      'AUTH_ERROR': 'HIGH',
      'UX': 'LOW',
      'SECURITY': 'CRITICAL'
    };
    return severityMap[type] || 'LOW';
  }

  checkAlerts() {
    // Check for alert patterns and escalate if needed
    const recentAlerts = this.alerts.filter(alert => {
      const alertTime = new Date(alert.timestamp);
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
      return alertTime > fiveMinutesAgo;
    });

    // Check for high error rate
    const errorAlerts = recentAlerts.filter(alert => alert.type === 'API_ERROR');
    if (errorAlerts.length > 5) {
      this.addAlert('SYSTEM', 'High error rate detected', {
        errorCount: errorAlerts.length,
        timeWindow: '5 minutes'
      });
    }

    // Check for performance degradation
    const performanceAlerts = recentAlerts.filter(alert => alert.type === 'PERFORMANCE');
    if (performanceAlerts.length > 3) {
      this.addAlert('SYSTEM', 'Performance degradation detected', {
        performanceIssueCount: performanceAlerts.length,
        timeWindow: '5 minutes'
      });
    }
  }

  checkErrorRate() {
    const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
    const recentErrors = this.criticalErrors.filter(error => error.timestamp > fiveMinutesAgo);
    
    const totalRequests = this.performanceMetrics.get('total_requests') || 0;
    if (totalRequests > 0) {
      const errorRate = recentErrors.length / totalRequests;
      
      if (errorRate > this.alertThresholds.errorRate) {
        this.addAlert('SYSTEM', 'Error rate exceeded threshold', {
          errorRate,
          threshold: this.alertThresholds.errorRate,
          errorCount: recentErrors.length,
          totalRequests
        });
      }
    }
  }

  // Utility Methods
  recordMetric(name, value) {
    this.performanceMetrics.set(name, value);
    
    // Send to monitoring service
    this.sendMetricToService(name, value);
  }

  recordAPIMetric(url, duration, status, error = null) {
    const metric = {
      url,
      duration,
      status,
      error,
      timestamp: new Date().toISOString()
    };
    
    this.performanceMetrics.set(`api_${url}`, metric);
    
    // Track total requests for error rate calculation
    const totalRequests = this.performanceMetrics.get('total_requests') || 0;
    this.performanceMetrics.set('total_requests', totalRequests + 1);
  }

  recordEvent(eventName, data) {
    captureEvent(eventName, data);
    this.sendEventToService(eventName, data);
  }

  trackError(error) {
    // Count errors by type
    const errorCount = this.errorCounts.get(error.type) || 0;
    this.errorCounts.set(error.type, errorCount + 1);
    
    // Store critical errors
    if (this.isCriticalError(error)) {
      this.criticalErrors.push(error);
      
      // Keep only last 50 critical errors
      if (this.criticalErrors.length > 50) {
        this.criticalErrors = this.criticalErrors.slice(-50);
      }
    }
    
    // Send to Sentry
    reportError(new Error(error.message), error);
    
    // Add alert for critical errors
    if (this.isCriticalError(error)) {
      this.addAlert('ERROR', `Critical error: ${error.message}`, error);
    }
  }

  isCriticalError(error) {
    const criticalPatterns = [
      /authentication/i,
      /payment/i,
      /database/i,
      /network request failed/i,
      /chunk load error/i,
      /security/i
    ];
    
    return criticalPatterns.some(pattern => pattern.test(error.message));
  }

  enrichErrorBeforeSend(event, _hint) {
    // Add custom context to errors
    event.contexts = {
      ...event.contexts,
      custom: {
        userId: this.getCurrentUserId(),
        tenantId: this.getCurrentTenantId(),
        sessionId: this.getSessionId(),
        errorCounts: Object.fromEntries(this.errorCounts),
        performanceMetrics: Object.fromEntries(this.performanceMetrics)
      }
    };
    
    return event;
  }

  enrichBreadcrumb(breadcrumb, _hint) {
    // Add custom data to breadcrumbs
    breadcrumb.data = {
      ...breadcrumb.data,
      userId: this.getCurrentUserId(),
      tenantId: this.getCurrentTenantId()
    };
    
    return breadcrumb;
  }

  getCurrentUserId() {
    try {
      const authData = JSON.parse(localStorage.getItem('auth_user') || '{}');
      return authData.id || 'anonymous';
    } catch (e) {
      return 'anonymous';
    }
  }

  getCurrentTenantId() {
    try {
      const tenantData = JSON.parse(localStorage.getItem('tenant_context') || '{}');
      return tenantData.id || 'unknown';
    } catch (e) {
      return 'unknown';
    }
  }

  getSessionId() {
    let sessionId = sessionStorage.getItem('monitoring_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('monitoring_session_id', sessionId);
    }
    return sessionId;
  }

  getReleaseVersion() {
    return (typeof import.meta !== 'undefined' && import.meta.env) ? (import.meta.env.VITE_APP_VERSION || '1.0.0') : (process.env.VITE_APP_VERSION || '1.0.0');
  }

  getDistribution() {
    return (typeof import.meta !== 'undefined' && import.meta.env) ? (import.meta.env.MODE || 'development') : (process.env.NODE_ENV || 'development');
  }

  sendAlertToService(alert) {
    // Send alert to monitoring service with retry logic
    this.sendWithRetry('/api/monitoring/alerts', alert, 'alert');
  }

  sendMetricToService(name, value) {
    // Send metric to monitoring service with retry logic
    const metricData = {
      name,
      value,
      timestamp: new Date().toISOString(),
      userId: this.getCurrentUserId(),
      tenantId: this.getCurrentTenantId()
    };
    this.sendWithRetry('/api/monitoring/metrics', metricData, 'metric');
  }

  sendEventToService(eventName, data) {
    // Send event to monitoring service with retry logic
    const eventData = {
      eventName,
      data,
      timestamp: new Date().toISOString(),
      userId: this.getCurrentUserId(),
      tenantId: this.getCurrentTenantId()
    };
    this.sendWithRetry('/api/monitoring/events', eventData, 'event');
  }

  /**
   * Send data to monitoring service with retry logic and fallback
   */
  async sendWithRetry(endpoint, data, dataType) {
    const maxRetries = 3;
    const retryDelay = 1000; // 1 second

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Tenant-ID': this.getCurrentTenantId()
          },
          body: JSON.stringify(data),
          signal: AbortSignal.timeout(5000) // 5 second timeout
        });

        if (response.ok) {
          console.log(`[EnhancedMonitoring] ${dataType} sent successfully`);
          return;
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      } catch (error) {
        console.warn(`[EnhancedMonitoring] Failed to send ${dataType} (attempt ${attempt}/${maxRetries}):`, error.message);
        
        if (attempt === maxRetries) {
          // Final attempt failed, store locally for later retry
          this.storeForLaterRetry(endpoint, data, dataType);
          return;
        }
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
      }
    }
  }

  /**
   * Store failed requests for later retry
   */
  storeForLaterRetry(endpoint, data, dataType) {
    try {
      const failedRequests = JSON.parse(localStorage.getItem('failed_monitoring_requests') || '[]');
      failedRequests.push({
        endpoint,
        data,
        dataType,
        timestamp: new Date().toISOString(),
        retryCount: 0
      });
      
      // Keep only last 50 failed requests
      if (failedRequests.length > 50) {
        failedRequests.splice(0, failedRequests.length - 50);
      }
      
      localStorage.setItem('failed_monitoring_requests', JSON.stringify(failedRequests));
      console.log(`[EnhancedMonitoring] ${dataType} stored for later retry`);
    } catch (error) {
      console.error('[EnhancedMonitoring] Failed to store for later retry:', error);
    }
  }

  /**
   * Retry failed requests
   */
  async retryFailedRequests() {
    try {
      const failedRequests = JSON.parse(localStorage.getItem('failed_monitoring_requests') || '[]');
      const remainingRequests = [];

      for (const request of failedRequests) {
        if (request.retryCount >= 3) {
          // Skip requests that have been retried too many times
          continue;
        }

        try {
          const response = await fetch(request.endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Tenant-ID': this.getCurrentTenantId()
            },
            body: JSON.stringify(request.data),
            signal: AbortSignal.timeout(5000)
          });

          if (response.ok) {
            console.log(`[EnhancedMonitoring] Retried ${request.dataType} sent successfully`);
          } else {
            // Increment retry count and keep for next time
            request.retryCount++;
            remainingRequests.push(request);
          }
        } catch (error) {
          // Increment retry count and keep for next time
          request.retryCount++;
          remainingRequests.push(request);
        }
      }

      localStorage.setItem('failed_monitoring_requests', JSON.stringify(remainingRequests));
    } catch (error) {
      console.error('[EnhancedMonitoring] Failed to retry failed requests:', error);
    }
  }

  // Public API
  getMetrics() {
    return {
      performanceMetrics: Object.fromEntries(this.performanceMetrics),
      errorCounts: Object.fromEntries(this.errorCounts),
      criticalErrors: this.criticalErrors,
      alerts: this.alerts,
      userFlows: Object.fromEntries(this.userFlows)
    };
  }

  getHealthStatus() {
    const recentAlerts = this.alerts.filter(alert => {
      const alertTime = new Date(alert.timestamp);
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
      return alertTime > fiveMinutesAgo;
    });

    const criticalAlerts = recentAlerts.filter(alert => alert.severity === 'CRITICAL');
    
    return {
      status: criticalAlerts.length > 0 ? 'CRITICAL' : 
              recentAlerts.length > 5 ? 'DEGRADED' : 'HEALTHY',
      alerts: recentAlerts,
      metrics: this.getMetrics()
    };
  }
}

// Create and export singleton instance
const enhancedMonitoring = new EnhancedMonitoring();

export default enhancedMonitoring;

// Export specific functions for easy use
export const {
  trackUserFlow,
  startUserFlow,
  completeUserFlowStep,
  addAlert,
  getMetrics,
  getHealthStatus
} = enhancedMonitoring;